import { useEffect, useState } from "preact/hooks";
import { api } from "./api";
import { getEffectiveRealtimeEndDate } from "./dateRange";
import {
  appendDashboardFiltersToSearchParams,
  dashboardFilters,
  dateRangePreset,
  endDate,
  selectedProperty,
  startDate,
} from "./state";

type EventSummaryRow = {
  event_name: string;
  total: number;
  anonymous_events: number;
  pseudonymous_events: number;
};

type EventsProps = {
  liveRefreshTick?: number;
};

const DEFAULT_EVENT_FILTER = "pageview";

const formatEventLabel = (eventName: string) => {
  if (eventName === "pageview") {
    return "Pageview";
  }

  if (eventName === "form_submit") {
    return "Form submit";
  }

  if (eventName === "consent_refused") {
    return "Consent refused";
  }

  if (eventName === "consent_revoked") {
    return "Consent revoked";
  }

  return eventName;
};

const Events = ({ liveRefreshTick = 0 }: EventsProps) => {
  const [loading, setLoading] = useState(true);
  const [eventsData, setEventsData] = useState<EventSummaryRow[]>([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [anonymousEvents, setAnonymousEvents] = useState(0);
  const [pseudonymousEvents, setPseudonymousEvents] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState(DEFAULT_EVENT_FILTER);

  const loadEvents = async (showLoading = true) => {
    if (!selectedProperty.value) {
      return;
    }

    if (showLoading) {
      setLoading(true);
    }

    try {
      const effectiveEndDate = getEffectiveRealtimeEndDate(endDate.value);
      const params = new URLSearchParams({
        property: selectedProperty.value,
        start: startDate.value,
        end: effectiveEndDate,
        limit: "12",
      });
      appendDashboardFiltersToSearchParams(params);

      const result = await api(`/api/events-summary?${params.toString()}`);
      setEventsData(Array.isArray(result.data) ? result.data : []);
      setTotalEvents(result.totalEvents ?? 0);
      setAnonymousEvents(result.anonymousEvents ?? 0);
      setPseudonymousEvents(result.pseudonymousEvents ?? 0);
    } catch (error) {
      console.error("Error fetching events summary:", error);
      setEventsData([]);
      setTotalEvents(0);
      setAnonymousEvents(0);
      setPseudonymousEvents(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedProperty.value) {
      loadEvents(true).catch(() => {
        setLoading(false);
      });
    }
  }, [selectedProperty.value, startDate.value, endDate.value, JSON.stringify(dashboardFilters.value)]);

  useEffect(() => {
    if (!selectedProperty.value) {
      return;
    }

    if (dateRangePreset.value !== "live" || liveRefreshTick === 0) {
      return;
    }

    loadEvents(false).catch((error) => {
      console.error("Error refreshing events summary:", error);
    });
  }, [liveRefreshTick, JSON.stringify(dashboardFilters.value)]);

  const names = [...new Set(eventsData.map((event) => event.event_name).filter(Boolean))];
  const otherNames = names
    .filter((name) => name !== DEFAULT_EVENT_FILTER)
    .sort((left, right) => left.localeCompare(right));
  const eventOptions = [DEFAULT_EVENT_FILTER, ...otherNames];

  const selectedSummary = selectedEvent
    ? eventsData.find((event) => event.event_name === selectedEvent) ?? {
        event_name: selectedEvent,
        total: 0,
        anonymous_events: 0,
        pseudonymous_events: 0,
      }
    : {
        event_name: "",
        total: totalEvents,
        anonymous_events: anonymousEvents,
        pseudonymous_events: pseudonymousEvents,
      };

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h3 className="card-title">Eventi tracciati</h3>
          <p className="section-note">
            Anche gli eventi anonimi custom compaiono qui; il filtro parte da `pageview`.
          </p>
        </div>
        <div style={{ minWidth: "200px" }}>
          <label
            htmlFor="dashboard-event-filter"
            style={{ display: "block", fontSize: "12px", marginBottom: "6px", color: "#666" }}
          >
            Filtro evento
          </label>
          <select
            id="dashboard-event-filter"
            value={selectedEvent}
            onChange={(event) =>
              setSelectedEvent((event.target as HTMLSelectElement).value)
            }
            style={{ width: "100%" }}
          >
            <option value="">Tutti gli eventi</option>
            {eventOptions.map((eventName) => (
              <option key={eventName} value={eventName}>
                {formatEventLabel(eventName)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="stats-row aggregate-stats-row">
        <div className="stat-item">
          <div className="stat-label">
            {selectedEvent ? formatEventLabel(selectedEvent) : "Tutti gli eventi"}
          </div>
          <div className="stat-value">{loading ? "-" : selectedSummary.total.toLocaleString()}</div>
        </div>
        <div className="stat-item">
          <div className="stat-label">Anonimi</div>
          <div className="stat-value">
            {loading ? "-" : selectedSummary.anonymous_events.toLocaleString()}
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-label">Pseudonimi</div>
          <div className="stat-value">
            {loading ? "-" : selectedSummary.pseudonymous_events.toLocaleString()}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
        </div>
      ) : eventsData.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🧭</div>
          <p className="empty-state-text">Nessun evento trovato nel periodo selezionato</p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Evento</th>
              <th className="table-cell-number">Totale</th>
              <th className="table-cell-number">Anonimi</th>
              <th className="table-cell-number">Pseudonimi</th>
            </tr>
          </thead>
          <tbody>
            {eventsData.map((event) => (
              <tr
                key={event.event_name}
                className={`filterable-row ${selectedEvent === event.event_name ? "is-filter-active" : ""}`}
                onClick={() => setSelectedEvent(event.event_name)}
              >
                <td className="table-cell-primary">{formatEventLabel(event.event_name)}</td>
                <td className="table-cell-number">{event.total}</td>
                <td className="table-cell-number">{event.anonymous_events}</td>
                <td className="table-cell-number">{event.pseudonymous_events}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Events;
